import { get, post, put } from '../data/client'
import { parseSdl } from './sdl'
import type { Manifest } from './protocol/manifest'

export const BUILD_PREFIX = 'ddws-build.invalid/input:'
export const BUILD_CAPABILITY = 'ddws.build.v1'
export const BUILD_BILLING_NOTICE = 'Rental billing starts at allocation and continues during image creation, including failed builds until automatic closure is confirmed. Build files, extracted source, cache, images and application data must fit in your rented storage. Builds time out after 30 minutes.'
export interface BuildCapability {
  enabled: boolean
  isolationProfile?: string
  availableStorageBytes?: number
  unavailableReason?: string
}

/** Re-read the selected provider before asking a wallet to authorize a rental. */
export async function requireBuildCapability(provider: string, sdl: string): Promise<void> {
  if (sdlBuildImages(sdl).length === 0) return
  if (!provider) throw new Error('Select a provider that supports Dockerfile builds before approving this rental.')
  let capability: BuildCapability
  try { capability = await get<BuildCapability>(`/api/tenant/build-inputs/capability/${encodeURIComponent(provider)}`) }
  catch { throw new Error('Dockerfile build availability could not be verified. Retry before approving this rental.') }
  if (capability?.enabled !== true) throw new Error(capability?.unavailableReason || 'Dockerfile builds are unavailable on this provider. Choose another provider or retry after its backend is ready.')
}

export interface BuildInput {
  id: string
  expiresAt?: number | undefined
  image: string
  inputBytes: number
  minimumStorageBytes: number
  dockerfileHash: string
  contextHash: string
  inputSizes?: { dockerfile: number; context: number }
  reservation?: { id: string; ticket: string; wallet: string; dseq: string }
}
export interface BuildStatus {
  phase: 'waiting' | 'building' | 'starting' | 'running' | 'failed'
  error?: string
  received?: { dockerfile: number; context: number }
  image?: string
  imageDigest?: string
}
export function isBuildImage(image: string | null | undefined): boolean { return Boolean(image?.startsWith(BUILD_PREFIX)) }
export function sdlBuildImages(sdl: string): string[] {
  return Object.values(parseSdl(sdl).services ?? {}).flatMap((service) => {
    const image = typeof service === 'object' && service !== null ? (service as { image?: unknown }).image : undefined
    return typeof image === 'string' && isBuildImage(image) ? [image] : []
  })
}
export function manifestBuildImages(json: string): string[] {
  return (JSON.parse(json) as Manifest).flatMap((group) => group.services.map((service) => service.image)).filter(isBuildImage)
}
export function buildErrors(draft: { workloadSource?: string | undefined; buildInput?: BuildInput | undefined; image: string; storage: number; autoRecovery: boolean; replicaCount: number; volume?: unknown }): string | null {
  if (draft.workloadSource !== 'dockerfile' && !isBuildImage(draft.image)) return null
  if (!draft.buildInput || draft.image !== draft.buildInput.image) return 'Prepare your Dockerfile and optional ZIP before continuing.'
  if (draft.buildInput.minimumStorageBytes >= draft.storage * 1024 ** 3) return 'Increase rented storage to leave room for image layers and application data.'
  if (draft.autoRecovery || draft.replicaCount !== 1 || draft.volume) return 'Dockerfile builds support one service, one replica and one rental disk.'
  return null
}

/** Reserve the exact captured source before the wallet can approve billing. */
export async function reserveBuildInput(input: BuildInput | undefined, wallet: string, dseq: bigint | string, sdl?: string): Promise<BuildInput | undefined> {
  const images = sdl === undefined ? input ? [input.image] : [] : sdlBuildImages(sdl)
  if (images.length === 0) return undefined
  if (!input || images.some((image) => image !== input.image)) throw new Error('Prepare the Dockerfile inputs matching this configuration before renting.')
  const result = await post<{ reservation: NonNullable<BuildInput['reservation']>; inputSizes: { dockerfile: number; context: number } }>(`/api/tenant/build-inputs/${input.id}/reservations`, { wallet, dseq: String(dseq), image: input.image })
  return { ...input, ...result }
}

/** Keep a live signing operation reserved, then revalidate after signing and before relay. */
export function protectBuildReservation(input: BuildInput | undefined): { beforeBroadcast: () => Promise<void>; stop: () => void } {
  if (!input) return { beforeBroadcast: async () => undefined, stop: () => undefined }
  const reservation = input.reservation
  if (!reservation) throw new Error('Reserve the captured Dockerfile inputs before renting.')
  const base = `/api/tenant/build-inputs/${input.id}/reservations/${reservation.id}`
  let pending: Promise<unknown> | null = null
  let stopped = false
  const timer = setInterval(() => {
    if (pending || stopped) return
    pending = post(`${base}/renew`, { ticket: reservation.ticket }).catch(() => undefined).finally(() => { pending = null })
  }, 30_000)
  const stop = () => { stopped = true; clearInterval(timer) }
  return {
    stop,
    beforeBroadcast: async () => {
      stop()
      await pending
      // A stale/removed reservation aborts before signed bytes are relayed.
      await post(`${base}/commit`, { ticket: reservation.ticket })
    },
  }
}

export async function prepareBuildInputs(dockerfile: Blob, context: File | null, storageBytes: number, progress: (value: string) => void, resumeId?: string, onCreated?: (id: string) => void): Promise<BuildInput> {
  if (dockerfile.size + (context?.size ?? 0) >= storageBytes) throw new Error('Inputs need more rented storage. Increase storage or reduce inputs.')
  const created = resumeId ? { id: resumeId } : await post<{ id: string }>('/api/tenant/build-inputs', { storageBytes })
  onCreated?.(created.id)
  const base = `/api/tenant/build-inputs/${created.id}`
  const state = await get<{ received: Record<string, number> }>(base)
  for (const [kind, file] of [['dockerfile', dockerfile], ['context', context]] as const) {
    if (!file) continue
    let offset = state.received[kind] ?? 0
    while (offset < file.size) {
      progress(`Uploading ${kind}: ${Math.floor(offset / file.size * 100)}%`)
      const chunk = file.slice(offset, offset + 1024 * 1024)
      const result = await put<{ offset: number }>(`${base}/${kind}`, chunk, { headers: { 'X-Build-Offset': String(offset) }, inactivityMs: 30 * 60_000 })
      if (!Number.isSafeInteger(result.offset) || result.offset <= offset || result.offset > offset + chunk.size) throw new Error('The upload did not advance. Retry preparing the same inputs to resume.')
      offset = result.offset
    }
  }
  progress('Checking build inputs…')
  let result = await post<BuildInput & { phase?: string }>(`${base}/complete`, {})
  while (!result.image) {
    progress(result.phase === 'queued' ? 'Waiting for build input verification…' : 'Checking build inputs…')
    await new Promise((resolve) => setTimeout(resolve, 2_000))
    result = await get<BuildInput & { phase?: string }>(base)
  }
  progress('Build inputs prepared')
  return result
}

export async function dispatchBuild(input: BuildInput, provider: string, dseq: string, gseq: number, oseq: number, token: string, renewToken?: () => Promise<string>): Promise<void> {
  const lease = `/api/tenant/provider/${encodeURIComponent(provider)}/lease/${encodeURIComponent(dseq)}/${gseq}/${oseq}/build`
  const dispatch = `/api/tenant/build-inputs/${input.id}/dispatch/${encodeURIComponent(provider)}/${encodeURIComponent(dseq)}/${gseq}/${oseq}`
  let currentToken = token
  let progress = 0
  let lastRenewal = -1
  const reservation = input.reservation
  if (!reservation || reservation.dseq !== dseq) throw new Error('The saved source reservation for this rental is unavailable. Return to the browser that initiated it.')
  const options = () => ({ headers: { Authorization: `Bearer ${currentToken}`, 'X-Build-Reservation': reservation.id, 'X-Build-Ticket': reservation.ticket }, timeoutMs: 35_000 })
  const renew = async () => {
    if (!renewToken || lastRenewal === progress) return false
    lastRenewal = progress
    currentToken = await renewToken()
    return true
  }
  const readState = async (): Promise<BuildStatus> => {
    let result: BuildStatus
    try { result = await get<BuildStatus>(`${lease}/status`, options()) }
    catch (cause) {
      if ((cause as { status: number }).status !== 401 || !await renew()) throw cause
      result = await get<BuildStatus>(`${lease}/status`, options())
    }
    if (result.image !== input.image) throw new Error('The saved build inputs differ from this rental’s signed configuration.')
    return result
  }
  const backoff = (attempt: number) => new Promise((resolve) => setTimeout(resolve, Math.min(5_000, 1_000 * 2 ** Math.min(attempt, 3))))
  let state: BuildStatus | undefined
  for (let attempt = 0; attempt < 60; attempt += 1) {
    try { state = await readState(); break } catch (cause) {
      if (![404, 429, 502, 503].includes((cause as { status: number }).status) || attempt === 59) throw cause
      await backoff(attempt)
    }
  }
  if (!state) throw new Error('The build runtime is not reporting yet. Retry Start workload to resume the upload.')
  if (state.phase !== 'waiting') return
  const staged = input.inputSizes ? { received: input.inputSizes } : await get<{ received: Record<string, number> }>(`/api/tenant/build-inputs/${input.id}`)
  for (const kind of ['dockerfile', 'context'] as const) {
    let offset = state.received?.[kind] ?? 0
    const total = staged.received[kind] ?? 0
    let busy = 0
    while (offset < total) {
      try {
        const result = await post<{ offset: number }>(dispatch, { kind, offset }, { ...options(), timeoutMs: null })
        if (!Number.isSafeInteger(result.offset) || result.offset <= offset || result.offset > total) throw new Error('The host upload did not advance. Retry Start workload to resume.')
        progress += result.offset - offset
        offset = result.offset
        busy = 0
      } catch (cause) {
        const status = (cause as { status?: number }).status
        if (status === 401) {
          if (!await renew()) throw cause
        } else if ([409, 429, 502, 503].includes(status ?? 0) && busy < 60) {
          await backoff(busy++)
        } else throw cause
        // A cancelled/competing hop may already have written part of this
        // chunk. Reconcile the host offset before sending any bytes again.
        state = await readState()
        if (state.phase !== 'waiting') return
        const received = state.received?.[kind] ?? 0
        if (!Number.isSafeInteger(received) || received < offset || received > total) throw new Error('The host returned an inconsistent upload offset. Retry Start workload to resume.')
        progress += received - offset
        offset = received
      }
    }
  }
  for (let attempt = 0; attempt < 60; attempt += 1) {
    try {
      await post(dispatch, { kind: 'start' }, options())
      return
    } catch (cause) {
      const status = (cause as { status?: number }).status
      if (status === 401) {
        if (!await renew()) throw cause
      } else if ((status === 429 || status === 503) && attempt < 59) await backoff(attempt)
      else throw cause
      state = await readState()
      if (state.phase !== 'waiting') return
    }
  }
  throw new Error('The host is busy. Retry Start workload to resume.')
}

export function deployedBuild(owner: string, dseq: string): BuildInput | null {
  try { return JSON.parse(window.localStorage.getItem(`ddws.console.deployed-build.${owner}.${dseq}`) ?? 'null') as BuildInput | null } catch { return null }
}
